import { useState, useEffect } from "react";
import "./App.css";
import "./style.css";
import {
  ButtonBigWithIcon,
  LoadingWait,
  PageLoading,
} from "./components";
import { AiOutlineShop } from "react-icons/ai";
import { TbCross } from "react-icons/tb";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import AppShell, { titleForPath } from "./components/AppShell";
import { ThemeRoot } from "./theme/BrandingProvider";
import logo from "./images/logo.png";

import * as Pages from "./pages";
import DashBoard from "./pages/dashboard";
import { WorkOrderListPage } from "./pages/workOrderListPage";
import { WorkOrdersRedesign } from "./pages/workOrdersRedesign";
import { RunSheetBuilderPage } from "./pages/runSheetBuilderPage";
import { ProductPage } from "./pages/productListPage";
import { RecurringInputPage } from "./pages/funeralOrderInputPage";
import { AddNewProductPage } from "./pages/newProduct";
// import {EditProductPage} from './pages/editProduct'
import { DelieryRunList } from "./pages/deliveryRunList";
import { InventoryList } from "./pages/inventoryList";
import { InventoryRedesign } from "./pages/inventoryRedesign";
import { UsersList } from "./pages/users";
import { UsersRedesign } from "./pages/usersRedesign";
import { GroupUsersPage } from "./pages/groupUsersPage";
import { GroupProductsPage } from "./pages/groupProductsPage";
import { GroupFdsPage } from "./pages/groupFdsPage";
import { WorkHistory } from "./pages/workHistory";
import { WorkHistoryRedesign } from "./pages/workHistoryRedesign";
import { CustomersHistory } from "./pages/customersHistory";
import { CustomersHistoryRedesign } from "./pages/customersHistoryRedesign";
import { SetupPage } from "./pages/setupPage";
import { SettingsRedesign } from "./pages/settingsRedesign";
import { OverviewPage } from "./pages/overviewPage";
import { NewOrderPage } from "./pages/newOrderPage";
import { DashboardRedesign } from "./pages/dashboardRedesign";
import { FDs } from "./pages/funeral_directors";
import { LogInPage } from "./pages/logInPage";
import { Print } from "./pages/Print";
import WorkOrdersPrintPage from "./pages/workOrdersPrintPage";
import RunSheetPrintPage from "./pages/runSheetPrintPage";
import WorkOrderPrintPage from "./pages/workOrderPrintPage";
import { Test } from "./test";
import { BUpage } from "./pages/backup.js";
import { BASE_SERVER_URL } from "./sampleData";
import axios from "./axiosConfig";
import { OrderedProducts } from "./pages/OrderedProducts.js";
import { OrderedProductsRedesign } from "./pages/orderedProductsRedesign";
import { DeliveryRunsRedesign } from "./pages/deliveryRunsRedesign";
import { ProductsRedesign } from "./pages/productsRedesign";
import { ProductEditorRedesign } from "./pages/productEditorRedesign";
import { FuneralDirectorsRedesign } from "./pages/funeralDirectorsRedesign";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { SwitchCoverGate } from "./businessSwitchCover";
import ProtectedRoute from "./auth/ProtectedRoute";
import { can } from "./auth/permissions";
import { NotificationsProvider } from "./notifications/NotificationsContext";
import { NotificationsPage } from "./pages/notificationsPage";
import {
  TransferDetailPage,
  TransfersPage,
} from "./pages/transfersPage";

/* App-wide chrome: the redesign's AppShell (sidebar + top bar) around every
   routed page. Replaces BasePage (legacy Header + SideNavigationSlider) —
   those components are kept in components.js but no longer rendered here.
   Bare paths render without the shell: print/test/standalone pages, login,
   and /dashboard-v2, which mounts its own AppShell to supply live nav counts. */
const BARE_PATHS = ["/print/", "/test", "/overview", "/dashboard-v2", "/login"];

/* Popped-out work windows: no sidebar, thin logo-only header. */
/* The run sheet builder pops out in its own window: no sidebar there. */
const isSlimPath = (path) =>
  path === "/run-sheet/new" || /^\/run-sheet\/[^/]+\/edit$/.test(path);

function ShellChrome({ user, header, bare, logout, children }) {
  const location = useLocation();
  const skip =
    !user ||
    !header ||
    bare ||
    BARE_PATHS.some((each) => location.pathname.indexOf(each) !== -1);
  // AppShell is ALWAYS rendered — `bare` hides the chrome but keeps the same
  // tree shape, so pages don't remount (and lose state) when chrome toggles.
  return (
    <AppShell
      bare={skip}
      slim={isSlimPath(location.pathname)}
      title={titleForPath(location.pathname)}
      user={user}
      onLogout={logout}
    >
      {children}
    </AppShell>
  );
}

function RaysApp() {
  const { user, logout } = useAuth();
  const [header, setHeader] = useState(true);
  const [globalState, setGlobalState] = useState(false);
  const [settings, setSetting] = useState({});

  useEffect(() => {
    // /settings/getAll needs settings:read now — only fetch once signed in,
    // and skip it for roles (drivers) that would only get a 403.
    if (!user || !can(user, "settings:read")) return;
    let url = BASE_SERVER_URL + "/settings/getAll";
    axios.get(url).then((doc) => {
      if (doc.data.status === "success") {
        setSetting(doc.data.response[0] || {});
      }
    });
  }, [user]);

  useEffect(() => {
    const onStorage = () => {
      if (localStorage.getItem("run-sheet-state") === "closed") {
        setGlobalState(false);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <div>
      <div className="app-content absolute-center">
        <ShellChrome
          user={user}
          header={header}
          bare={globalState}
          logout={logout}
        >
        <Routes>
          <Route element={<ProtectedRoute />}>
            <Route path={"/test"} element={<Test title="Test" />} />
            {/* The redesigned dashboard is the landing page; the legacy one
                stays reachable at /dashboard-v1 for comparison. */}
            <Route path={"/"} element={<Navigate to="/dashboard-v2" replace />} />
            <Route
              path={"/dashboard-v1"}
              element={
                <DashBoard currentUser={user} title="DashBoard" />
              }
            />
            {/* <Route path={'/'} element={<WorkOrderListPage orderType={'Funeral'} title='Work Orders'/>}/> */}
            {/* Redesigned settings. The legacy setup screen stays reachable
                at /settings-v1, and the mock that briefly lived at
                /settings-v2 redirects here. */}
            {can(user, "settings:read") && (
              <Route path={"/settings"} element={<SettingsRedesign />} />
            )}
            {can(user, "settings:read") && (
              <Route
                path={"/settings-v1"}
                element={<SetupPage title="Setting" />}
              />
            )}
            {can(user, "settings:read") && (
              <Route
                path={"/settings-v2"}
                element={<Navigate to="/settings" replace />}
              />
            )}
            <Route path={"/overview"} element={<OverviewPage />} />
            {/* Users across every business, reached from the overview. Bare
                chrome (matches the /overview prefix in BARE_PATHS); the
                page says per business whether the caller can manage. */}
            <Route path={"/overview/users"} element={<GroupUsersPage />} />
            {/* Products across every business plus the global (shared)
                ones, reached from the overview's Products card. */}
            <Route path={"/overview/products"} element={<GroupProductsPage />} />
            <Route path={"/overview/fds"} element={<GroupFdsPage />} />
            <Route path={"/dashboard-v2"} element={<DashboardRedesign />} />
            {/* Redesigned work order list. The legacy list stays reachable
                at /WorkOrderList-v1 for comparison; every legacy work-order
                URL below redirects here. */}
            <Route path={"/work-orders"} element={<WorkOrdersRedesign />} />
            <Route path={"/run-sheet/new"} element={<RunSheetBuilderPage />} />
            <Route
              path={"/run-sheet/:id/edit"}
              element={<RunSheetBuilderPage editRun={true} />}
            />
            <Route
              path={"/WorkOrderList-v1"}
              element={
                <WorkOrderListPage
                  ChangeGlobalState={(a) => {
                    setGlobalState(a);
                  }}
                  orderType={"Funeral"}
                  title="Work Orders"
                />
              }
            />
            {/* Legacy routes → redesigned equivalents (old pages are kept,
                only the URLs move). */}
            <Route
              path={"/WorkOrderList"}
              element={<Navigate to="/work-orders" replace />}
            />
            <Route
              path={"/WorkOrderList/funeral"}
              element={<Navigate to="/work-orders?type=Funeral" replace />}
            />
            <Route
              path={"/WorkOrderList/retail"}
              element={<Navigate to="/work-orders?type=Retail" replace />}
            />
            <Route
              path={"/WorkOrderList/newRunSheet"}
              element={<Navigate to="/work-orders?run=1" replace />}
            />
            <Route
              path={"/work-order/:type/:serial"}
              element={<RedirectWorkOrder />}
            />
            <Route
              path={"/editRunSheet/:runId/:runSheetDate"}
              element={<RedirectRunEdit />}
            />
            <Route
              path="/ChooseOrderType"
              element={<ChooseOrderType title="Input Order" />}
            />
            <Route
              path="/FuneralOrderInput"
              element={
                <Pages.FuneralInputPage
                  current_user={user}
                  title="Funeral Order Input"
                  orderType="Funeral"
                />
              }
            />
            {/* Redesigned order entry, alongside /FuneralOrderInput. Same
                endpoints and the same /wo/addNew payload — a new screen, not
                a replacement. */}
            <Route
              path="/new-order"
              element={
                <NewOrderPage current_user={user} title="New work order" />
              }
            />
            {/* Redesigned ordered products; the legacy table stays
                reachable at /ordered-products-v1 for comparison. */}
            <Route
              path="/ordered-products"
              element={<OrderedProductsRedesign />}
            />
            <Route
              path="/ordered-products-v1"
              element={
                <OrderedProducts
                  fromWO={true}
                  settings={settings || {}}
                  current_user={user}
                  title="Ordered Products"
                  orderType="Funeral"
                />
              }
            />
            <Route
              path={"/RetailOrderInput"}
              element={
                <RecurringInputPage
                  current_user={user}
                  title="Retail Order Input"
                  orderType="Retail"
                />
              }
            />
            <Route
              path="/editworkorder/funeral/:id"
              element={
                <NewOrderPage
                  key="edit-funeral"
                  current_user={user}
                  title="Edit work order"
                  editWo={true}
                  orderType="Funeral"
                />
              }
            />
            <Route
              path="/editworkorder/retail/:id"
              element={
                <NewOrderPage
                  key="edit-retail"
                  current_user={user}
                  title="Edit work order"
                  editWo={true}
                  orderType="Retail"
                />
              }
            />
            {/* Redesigned delivery runs + detail modal. The legacy list
                stays reachable at /DeliveryRunList-v1; the old URL
                redirects here. */}
            <Route path={"/delivery-runs"} element={<DeliveryRunsRedesign />} />
            <Route
              path={"/DeliveryRunList"}
              element={<Navigate to="/delivery-runs" replace />}
            />
            <Route
              path={"/DeliveryRunList-v1"}
              element={<DelieryRunList title="Delivery Run Sheets" />}
            />
            {/* Redesigned flower inventory. The legacy list stays reachable
                at /InventoryList-v1; the old URL redirects here. */}
            <Route path={"/inventory"} element={<InventoryRedesign />} />
            <Route
              path={"/InventoryList"}
              element={<Navigate to="/inventory" replace />}
            />
            <Route
              path={"/InventoryList-v1"}
              element={<InventoryList title="Inventory List" />}
            />
            {/* Redesigned catalogue + editor. The legacy list stays at
                /products-v1 and the legacy form at /newProducts[/:id];
                both old URLs still resolve, they just aren't linked. */}
            <Route path={"/products"} element={<ProductsRedesign />} />
            <Route
              path={"/products/new"}
              element={<ProductEditorRedesign editProduct={false} />}
            />
            <Route
              path={"/products/:id/edit"}
              element={<ProductEditorRedesign editProduct={true} />}
            />
            <Route
              path={"/products-v1"}
              element={<ProductPage title="Products" />}
            />
            <Route
              path={"/newProducts"}
              element={
                <AddNewProductPage editProduct={false} title="Add Product" />
              }
            />
            <Route
              path={"/newProducts/:id"}
              element={
                <AddNewProductPage editProduct={true} title="Edit Product" />
              }
            />
            {/* Redesigned staff page (was /users); the legacy card list stays
                reachable at /users-v1 for comparison. */}
            {can(user, "users:manage") && (
              <Route path={"/staff"} element={<UsersRedesign />} />
            )}
            <Route path={"/users"} element={<Navigate to="/staff" replace />} />
            {can(user, "users:manage") && (
              <Route path={"/users-v1"} element={<UsersList />} />
            )}
            {/* Redesigned funeral directors; the legacy table stays at
                /FuneralDirectors-v1 and the old URL redirects. */}
            <Route
              path={"/funeral-directors"}
              element={<FuneralDirectorsRedesign />}
            />
            <Route
              path={"/FuneralDirectors"}
              element={<Navigate to="/funeral-directors" replace />}
            />
            <Route path={"/FuneralDirectors-v1"} element={<FDs />} />
            {/* Legacy run-sheet popup routes → the redesigned builder. The
                old popups only worked via a cross-tab localStorage protocol,
                so the no-id edit route lands on the run list instead. */}
            <Route
              path={"/RunSheetMaking"}
              element={<Navigate to="/run-sheet/new" replace />}
            />
            <Route
              path={"/EditRunSheet"}
              element={<Navigate to="/delivery-runs" replace />}
            />
            {/* Business notification feed (bell in the top bar) and the
                first thing it points at: work-order transfer requests
                from other businesses. The transfer pages are reachable by
                every member — they explain themselves to roles without
                workorders:transfer instead of 403ing. */}
            <Route path={"/notifications"} element={<NotificationsPage />} />
            <Route
              path={"/transfers"}
              element={<TransfersPage direction="incoming" />}
            />
            <Route
              path={"/transfers/outgoing"}
              element={<TransfersPage direction="outgoing" />}
            />
            {/* The first build of these screens was inbox-only; keep both
                URLs working — notifications sent before this deploy point
                at /transfers/incoming/:id. */}
            <Route
              path={"/transfers/incoming"}
              element={<Navigate to="/transfers" replace />}
            />
            <Route
              path={"/transfers/incoming/:id"}
              element={<RedirectTransfer />}
            />
            <Route path={"/transfers/:id"} element={<TransferDetailPage />} />
            {/* Redesigned history screens; the legacy pair stays reachable
                at /work-history-v1 and /customer-history-v1. */}
            <Route path={"/work-history"} element={<WorkHistoryRedesign />} />
            <Route path={"/work-history-v1"} element={<WorkHistory />} />
            <Route
              path={"/customer-history"}
              element={<CustomersHistoryRedesign />}
            />
            <Route
              path={"/customer-history-v1"}
              element={<CustomersHistory />}
            />
            <Route
              path={"/deliveries/print/:filter"}
              element={
                <Print
                  print="deliveries"
                  hideHeader={() => {
                    setHeader(false);
                  }}
                />
              }
            />
            <Route
              path={"/ordered-products/print/:filter"}
              element={
                <Print
                  print="ordered-products"
                  hideHeader={() => {
                    setHeader(false);
                  }}
                />
              }
            />
            <Route
              path={"/workorder/print/:filter/:date"}
              element={<WorkOrderPrintPage />}
            />
            <Route
              path={"/workorders/print/:filter"}
              element={<WorkOrdersPrintPage />}
            />
            <Route
              path={"/run-sheet/print/:filter"}
              element={<RunSheetPrintPage />}
            />
            {/* Routes above are dropped for roles without the permission —
                land those URLs on the dashboard instead of a blank page. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
          <Route path="/login" element={<LogInPage />} />
        </Routes>
        </ShellChrome>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      {/* lifts the business-switch cover once the new business has loaded */}
      <SwitchCoverGate />
      <BrowserRouter>
        {/* Needs the router (business id follows navigation, toasts
            navigate) and the auth context (polls only while signed in). */}
        <NotificationsProvider>
          {/* the open business's palette, on :root for pages, modals and print */}
          <ThemeRoot />
          <RaysApp />
        </NotificationsProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

/* Param-carrying legacy URLs can't redirect with a bare <Navigate>. */
function RedirectWorkOrder() {
  const { serial } = useParams();
  return <Navigate to={`/work-orders?order=${serial || ""}`} replace />;
}

/* Old edit links land on the list in edit mode — "Show run sheet" there
   pops the builder out (a redirect can't open a pop-up itself). */
function RedirectRunEdit() {
  const { runId } = useParams();
  return <Navigate to={`/work-orders?editRun=${runId}`} replace />;
}

function RedirectTransfer() {
  const { id } = useParams();
  return <Navigate to={`/transfers/${id}`} replace />;
}

function ChooseOrderType() {
  const navigate = useNavigate();
  return (
    <div className="order-type-select">
      <h3>Select Type of Order</h3>
      <span className="order-type">
        <ButtonBigWithIcon
          clickTo={() => {
            navigate("/new-order");
          }}
          value="Funeral"
          icon={<TbCross className="icon XL width-100" />}
        />
        <ButtonBigWithIcon
          clickTo={() => {
            navigate("/RetailOrderInput");
          }}
          value="Retail"
          icon={<AiOutlineShop className="icon XL width-100" />}
        />
        {/* <Comp.ButtonBigWithIcon clickTo={()=>{navigate('/RetailOrderInput')}} value='Corporate' icon={<IconsMD.MdCorporateFare className='icon XL width-100'/>}/> */}
      </span>
    </div>
  );
}

function ErrorLogPage() {
  const [data, setData] = useState([]);

  useEffect(() => {
    // /errorLogs now requires logs:read — go through the shared axios
    // instance so the Authorization header and refresh interceptor apply.
    axios
      .get(BASE_SERVER_URL + "/errorLogs")
      .then((doc) => {
        setData(doc.data || []);
      })
      .catch(() => {
        setData([]);
      });
  }, []);

  return (
    <div className="container">
      <div className="message header">
        <span className="text">Error Message</span>
        <span className="date">Error date</span>
      </div>
      {data.map((x, i) => {
        // console.log(x)
        return (
          <div key={i} className="message">
            <span className="text">{x.message}</span>
            <span className="date">{x.date}</span>
          </div>
        );
      })}
    </div>
  );
}

export default App;
